import React, {useEffect, useState} from 'react'
import {FullScreenDialog} from "../ui/modal-confirmation";
import {useGetUserQuery} from "../../apis/users.api";
import {classNames, formatRank, hasElements} from "../../utils/Helper";
import {QuantityCoinComponent} from "../ui/quantity-coin.component";
import {PodiumComponent} from "./podium.component";
import {ItemShadowIcon} from "../ui/item-shadow-icon";
import {motion} from "framer-motion";
import {GameCategory, GameNameType, GameNameValue, TournamentReward, TournamentRewardType, User} from "../../models/User";
import toast from "react-hot-toast";
import {InvestIcon} from '../svg/invest-icon';
import {BetsIcon} from '../svg/bets-icon';
import {LeagueName} from '../ranking/league.component';
import LeagueIcon from '../ui/league-icons.component';
import LoudlyCryingFaceIcon from '../svg/LoudlyCryingFaceIcon';
import LeagueRewardIcon from '../ranking/league-rewards-icons.components';
import {useGetTournamentRankingQuery} from "../../apis/tournaments.api";

export type LeagueResultFinalStatus = 'promotion' | 'demotion' | 'remains' | 'miss_promotion' | 'not_ready_promotion';

export interface LeagueResumeProps {
  user: User;
  game: GameCategory,
  rank: number,
  status: LeagueResultFinalStatus,
  league: number,
  nextLeague: number,
  coinScore: number,
  rewards: TournamentReward[],
  ackNotif: Function,
}

function notifSteps(payload: LeagueResumeProps) {
  // return (payload.rewards.length === 0 ? 2 : 3);
  return 3;
}

const LeagueResumeNotif: React.FC<LeagueResumeProps> = (payload) => {

  const [step, setStep] = useState(1);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (step > notifSteps(payload)) {
      setOpen(false);
      payload.ackNotif();
    } else {
      setOpen(true);
    }
  }, [step]);

  return <div id={"WeekResumeNotifComponent"}
              className={`relative flex justify-center flex-col items-center gap-y-6 w-full z-10`}>
    <FullScreenDialog name={"main"} displayOpenButton={false} openProp={open}
                      timer={step < notifSteps(payload) ? 10 : undefined}
                      autoClosing={false}
                      restartTimer={true}
                      showUpperCloseButton={step === notifSteps(payload)}
                      timerFinished={() => {
                        setStep((prev) => ++prev);
                      }} onClose={() => {
      toast.dismiss();
    }}>
      <>
        {step === 1 &&
          <StepRank payload={payload}/>
        }
        {step === 2 &&
          <StepLeague payload={payload}/>
        }
        {notifSteps(payload) === 3 && step === 3 &&
          <StepRewards payload={payload}/>
        }
      </>
    </FullScreenDialog>
  </div>
}

interface StepProps {
  payload: LeagueResumeProps;
}

const StepRank: React.FC<StepProps> = ({payload}) => {

  const [rank, setRank] = useState(0);
  const {data: user} = useGetUserQuery();
  useEffect(() => {
    const r = Number(payload.rank);
    setRank(r > 1 ? r - 2 : 0);
    // console.log(`setting rank to ${r > 2 ? r - 2 : 1}`)
  }, [payload]);

  const {data: playersData, isLoading: arePlayersLoading} = useGetTournamentRankingQuery({
    gameCategory: payload.game,
    offset: rank,
    pageSize: 3,
    cycle: 'previous',
  });

  const getCurrentCss = (pseudo: string) => {
    if (user && pseudo === user.pseudo) {
      return 'bg-black rounded-lg text-white z-10';
    } else return '';
  }

  const getStep1Title = (rewards: Array<{ type: string, value: number }>, status: LeagueResultFinalStatus) => {

    if (hasElements(rewards)) {
      return "Congratulations";
    }

    if (status == 'demotion') {
      return "Tough times";
    }

    return "Not bad";
  }

  return <div className={"h-full flex flex-col gap-y-4 justify-center items-center px-2"}>

    <div className={"flex-col flex gap-3 justify-center items-center"}>

      <GameName category={payload.game}/>

      <div className={"rounded-lg bg-black bg-opacity-20 text-white font-bold px-2 py-2 text-lg text-center"}>
        {getStep1Title(payload.rewards, payload.status)}
      </div>

      <div className={"text-white text-center font-normal text-sm opacity-80"}>
        {`You finished ${formatRank(payload.rank)} on the ${GameNameValue[payload.game]} Game leaderboard`}
      </div>

    </div>

    {Number(payload.rank) > 3 &&
      <div
        className={"relative overflow-hidden flex-col flex gap-3 justify-center items-center mx-2 bg-white py-1 px-2 rounded-lg flat-card w-full"}>
        <>
          <div
            className={"z-0 backdrop-blur-[1px] shadow-[inset_0px_0px_100px_30px_white] absolute left-0 right-0 top-0 bottom-0"}></div>
          {playersData && hasElements(playersData.players) && playersData.players.map(p => <div key={`${p.pseudo}`}
                                                                                                className={classNames("flex w-full mx-1 gap-x-3 items-center py-3 px-3 text-sm", getCurrentCss(p.pseudo))}>
            <span className={"w-6"}>{p.rank}</span>
            <div className={"flex items-center text-inherit gap-x-2"}>
              <div className={"p-0.5 bg-white shadow border border-neutral-200 w-8 h-8 rounded"}>
                <img src={p.avatarUrl || `${process.env.REACT_APP_STATIC_ASSETS}/favicon.svg`}
                     className="rounded-sm object-cover w-full h-full"/>
              </div>
              <span className={"text-inherit max-w-[103px] overflow-hidden overflow-ellipsis"}>{p.pseudo}</span>
            </div>
            <span className={"grow flex justify-end flex-col items-end"}>
                <QuantityCoinComponent quantity={payload.coinScore}/>
              {/* <div
                  className={classNames("text-green-500 text-[12px] font-bold font-['Satoshi'] leading-[10px]")}>
                  <GainComponent gain={(p.gain || 0)}/>
                </div> */}
              </span>
          </div>)}
        </>
      </div>
    }

    {Number(payload.rank) <= 3 &&
      <PodiumComponent firstPlayersData={playersData} navigation={true}/>
    }

  </div>
};

const StepLeague: React.FC<StepProps> = ({payload}) => {

  const getStep2Title = (status: LeagueResultFinalStatus) => {

    switch (status) {
      case 'remains':
        return "No rank update";
        break;
      case 'promotion':
      case 'not_ready_promotion':
        return "Well done!";
        break;
      case 'miss_promotion':
        return "Qualification missed!";
        break;
      case 'demotion':
        return "Rank relegation...";
        break;
    }

  }

  return <div className={"h-full flex flex-col gap-y-4 justify-center items-center px-5"}>

    <GameName category={payload.game}/>

    <div className={"flex flex-col justify-center items-center gap-y-2 text-center"}>

      <h1 className={"text-xl rounded-lg bg-black bg-opacity-10 text-white font-bold px-2 py-2 text-center"}>
        {getStep2Title(payload.status)}
      </h1>

      { payload.status === 'miss_promotion' && <>
        <p className={"font-normal opacity-80 text-white"}>
          {'You miss promotion because your have not enough collectibles cards'}
        </p>
      </>
      }

      { payload.status === 'not_ready_promotion' && <>
        <p className={"font-normal opacity-80 text-white"}>
          You are skilled enough to be promoted to <span className='capitalize'>{LeagueName[payload.nextLeague + 1]}</span> League as soon it will open.
        </p>
        <p className={"font-normal opacity-80 text-white"}>
          Keep scoring in the <span className='capitalize'>{LeagueName[payload.nextLeague]} league</span> for the next cycle.
        </p>
      </>
      }

      { ['promotion', 'demotion'].includes(payload.status) &&
        <p className={"font-normal opacity-80 text-white"}>
          Your will play in the <span className='capitalize'>{LeagueName[payload.nextLeague]} league</span> for the next cycle.
        </p>
      }

      { ['miss_promotion', 'remains'].includes(payload.status) &&
        <p className={"font-normal opacity-80 text-white"}>
          Your will continue to play in the <span className='capitalize'>{LeagueName[payload.nextLeague]} league</span> for the next cycle.
        </p>
      }

    </div>

    <div className={"flex flex-col gap-3 justify-center items-center"}>
      <motion.div
        className=""
        animate={{
          y: ["10%", "-10%", "10%"],
        }}
        transition={{
          duration: 3,
          ease: "easeInOut",
          repeat: Infinity,
        }}
      >
        <LeagueIcon leagueType={payload.nextLeague}/>
      </motion.div>
      <motion.div
        animate={{
          opacity: [0.7, 1, 0.7]
        }}
        transition={{
          duration: 3,
          ease: "easeInOut",
          repeat: Infinity,
        }}
      >
        <ItemShadowIcon/>
      </motion.div>
      <div className={"rounded-lg bg-black bg-opacity-50 px-4 py-2"}>
        <p className="text-white flex flex-row gap-2 justify-between items-center text-2xl font-BioRhyme uppercase">
          {LeagueName[payload.nextLeague]}
        </p>
      </div>
    </div>

  </div>
};

const StepRewards: React.FC<StepProps> = ({payload}) => {


  const [currentScore, setCurrentScore] = useState(0);

  function animateValue(start: number, end: number, duration: number) {
    let startTimestamp: number | null = null;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      setCurrentScore(Math.floor(progress * (end - start) + start));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }

  useEffect(() => {
    animateValue(0, 0, 2000);
  }, []);


  return <div className={"h-full flex flex-col gap-y-4 justify-center items-center px-5"}>

    <GameName category={payload.game}/>

    <div className={`w-full flex flex-col justify-center items-center gap-y-2`}>

      <h1 className={"text-2xl rounded-lg bg-black bg-opacity-10 text-white font-bold px-2 py-2 text-center"}>
        {!hasElements(payload.rewards) ? 'No league rewards' : 'Time for rewards'}
      </h1>

      {!hasElements(payload.rewards) &&
        <>
          <p className='font-normal opacity-80 text-white'>You'll do better next time!</p>
          <LoudlyCryingFaceIcon h={50}/>
        </>
      }

      {hasElements(payload.rewards) &&
        <>
          <p className='font-normal opacity-80 text-white mb-7'>
            TOP #{payload.rank} <span className='capitalize'>{LeagueName[payload.league]} league</span> rewards
          </p>

          <div className='w-full flex flex-wrap justify-center items-end gap-2 gap-y-4'>

            {
              payload.rewards.map(function(reward){
                return <LeagueRewards rewardType={reward.type} rewardAmount={reward.value}/>
              })
            }

          </div>

        </>
      }

    </div>

  </div>
};

export {LeagueResumeNotif};


interface GameNameProps {
  category: GameCategory
}

const GameName: React.FC<GameNameProps> = ({category}) => {

  return (

    <>
      {
        category === GameCategory.BET_GAME &&
        <div className='flex text-white gap-2 text-2xl center font-BioRhyme'>
          <BetsIcon h={50} color={"white"}/> {GameNameType.BET_GAME}
        </div>
      }

      {
        category === GameCategory.INVEST_GAME &&
        <div className='flex text-white gap-2 text-2xl center font-BioRhyme'>
          <InvestIcon h={50} color={"white"}/> {GameNameType.INVEST_GAME}
        </div>
      }


    </>
  );
}

interface LeagueRewardsProps {
  rewardType: TournamentRewardType,
  rewardAmount: number
}

const LeagueRewards: React.FC<LeagueRewardsProps> = ({rewardType, rewardAmount}) => {
  return (
    <div className={"flex flex-col gap-3 justify-center items-center"}>
      <motion.div
        className="h-[60px] flex flex-com items-center"
        animate={{
          y: ["10%", "-10%", "10%"],
        }}
        transition={{
          duration: 3,
          ease: "easeInOut",
          repeat: Infinity,
        }}
      >
        <LeagueRewardIcon type={rewardType} size="notification"/>
        {/* <GemsIcon h={48}/> */}
      </motion.div>
      <motion.div
        animate={{
          opacity: [0.7, 1, 0.7]
        }}
        transition={{
          duration: 3,
          ease: "easeInOut",
          repeat: Infinity,
        }}
      >
        <ItemShadowIcon/>

      </motion.div>
      <div className={"rounded-lg bg-black bg-opacity-50 text-white font-bold px-4 py-2 text-center text-xl"}>
        <span className='font-BioRhyme text-xl'>x{rewardAmount}</span>
      </div>

    </div>
  );
}